import { Injectable } from '@angular/core'
import { BehaviorSubject, Observable } from 'rxjs'
import { ICurrencyOptions } from '@core/models/currency.model'
import { CURRENCY_CONFIG } from '@core/constants/currency.constants'
import { REGEX_PATTERNS } from '@core/constants/regex.constants'

@Injectable({
    providedIn: 'root',
})
export class CurrencyService {
    private currentCurrencySubject = new BehaviorSubject<string>('INR')
    public currentCurrency$: Observable<string> = this.currentCurrencySubject.asObservable()

    constructor() {
        // Initialize with default currency or from localStorage
        const savedCurrency = localStorage.getItem('preferredCurrency')
        if (savedCurrency && CURRENCY_CONFIG[savedCurrency]) {
            this.setCurrency(savedCurrency)
        }
    }

    getCurrentCurrency(): string {
        return this.currentCurrencySubject.value
    }

    setCurrency(currencyCode: string): void {
        if (CURRENCY_CONFIG[currencyCode]) {
            this.currentCurrencySubject.next(currencyCode)
            localStorage.setItem('preferredCurrency', currencyCode)
        }
    }

    getCurrencyConfig(currencyCode?: string): ICurrencyOptions {
        const code = currencyCode || this.getCurrentCurrency()
        return CURRENCY_CONFIG[code] || CURRENCY_CONFIG['INR']
    }

    getAvailableCurrencies(): string[] {
        return Object.keys(CURRENCY_CONFIG)
    }

    formatCurrency(
        value: number | string,
        currencyCode?: string,
        options?: Partial<ICurrencyOptions>,
    ): string {
        const code = currencyCode || this.getCurrentCurrency()
        const config = this.getCurrencyConfig(code)
        const mergedOptions: ICurrencyOptions = { ...config, ...options }

        const numericValue = typeof value === 'string' ? parseFloat(value) : value

        if (isNaN(numericValue)) {
            return ''
        }

        const minimumFractionDigits =
            mergedOptions.minimumFractionDigits ?? mergedOptions.decimalPlaces ?? 0

        const maximumFractionDigits =
            mergedOptions.maximumFractionDigits ??
            mergedOptions.decimalPlaces ??
            minimumFractionDigits

        const formatter = new Intl.NumberFormat(mergedOptions.locale, {
            style: 'currency',
            currency: mergedOptions.currency,
            minimumFractionDigits,
            maximumFractionDigits,
        })

        let formattedValue = formatter.format(numericValue)

        if (mergedOptions.showSymbol === false) {
            formattedValue = formattedValue.replace(/[₹$€£]/g, '').trim()
        } else {
            // Ensure consistent spacing between the rupee symbol and the amount (handle Unicode spaces)
            formattedValue = String(formattedValue || '').replace(
                REGEX_PATTERNS.RUPEE_SYMBOL_WITH_SPACES,
                '₹ ',
            )
        }

        return formattedValue
    }
}
